import React, { useMemo } from 'react';
import { DocumentForm } from '../../documents/form/DocumentForm';
import { DocumentSchema, DocumentStatus } from '../../documents/types';
import { CaseReport } from '../../../services/reportService';
import { ReportProvider } from './ReportContext';
import { buildCptDocumentFunctions } from './services/cptDocumentFunctions';
import { CptReportPreview } from './CptReportPreview';
import { generateTextReport } from './utils/cptDocumentViewUtils';

interface CaseDocumentEditorProps {
    schema: DocumentSchema;
    schemaKey: string;
    schemaVersion?: number;
    dataSources: any;
    initialData: Record<string, any>;
    applicableCodes?: CaseReport['cptEligibleCodes'];
    externalContextData?: Record<string, any>;
    persistDataOverrides?: Record<string, any>;
    caseRecordId?: string;
    reportData?: { data?: Record<string, any>; status?: DocumentStatus };
    onPersisted?: (savedCase: CaseReport) => Promise<void> | void;
    onDataChange?: (data: Record<string, any>) => void;
}

const CaseDocumentEditorContent: React.FC<{
    schema: DocumentSchema;
    dataSources: any;
    applicableCodes?: CaseReport['cptEligibleCodes'];
    externalContextData?: Record<string, any>;
}> = ({ schema, dataSources, applicableCodes, externalContextData }) => {
    const cptFunctions = useMemo(() => buildCptDocumentFunctions(), []);
    const SingleDocumentPreviewAdapter: React.FC<{
        schema: DocumentSchema;
        data: any;
        dataSources?: any;
    }> = ({ schema: previewSchema, data: previewData, dataSources }) => {
        return (
            <CptReportPreview
                documents={[
                    {
                        schema: previewSchema,
                        data: previewData,
                        sourceLabel: previewSchema.title,
                    },
                ]}
                applicableCodes={applicableCodes}
                dataSources={dataSources}
            />
        );
    };

    return (
        <DocumentForm
            schema={schema}
            dataSources={dataSources}
            functions={cptFunctions}
            externalContextData={externalContextData}
            generatePreviewText={generateTextReport}
            PreviewComponent={SingleDocumentPreviewAdapter}
        />
    );
};

export const CaseDocumentEditor: React.FC<CaseDocumentEditorProps> = ({
    schema,
    schemaKey,
    schemaVersion,
    dataSources,
    initialData,
    applicableCodes,
    externalContextData,
    persistDataOverrides,
    caseRecordId,
    reportData,
    onPersisted,
    onDataChange,
}) => {
    return (
        <ReportProvider
            schemaKey={schemaKey}
            schemaVersion={schemaVersion}
            initialData={initialData}
            caseRecordId={caseRecordId}
            reportData={reportData}
            onPersisted={onPersisted}
            onDataChange={onDataChange}
            persistDataOverrides={persistDataOverrides}
        >
            <CaseDocumentEditorContent
                schema={schema}
                dataSources={dataSources}
                applicableCodes={applicableCodes}
                externalContextData={externalContextData}
            />
        </ReportProvider>
    );
};

export default CaseDocumentEditor;
